import React, { useState, useEffect } from "react";
import { Crown, ArrowUpCircle, CheckCircle, XCircle, Clock, ChevronDown, ChevronUp, History, Calendar, Shield } from "lucide-react";
import { getProfiles, getPaymentStatus } from "../services/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "../i18n/LanguageContext";
import { useAppConfig } from "../context/AppConfigContext";

export default function SubscriptionsPage() {
  const { t } = useTranslation();
  const { paymentsEnabled, tiers } = useAppConfig();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedHistory, setExpandedHistory] = useState({});
  const [resolvedPaymentStatus, setResolvedPaymentStatus] = useState(searchParams.get("payment"));

  useEffect(() => {
    const pid = searchParams.get("pid");
    const urlStatus = searchParams.get("payment");

    const loadData = async () => {
      try {
        const profRes = await getProfiles();
        setProfiles(profRes.data.profiles);
      } catch {}

      // If we have a payment ID, check real status from backend
      if (pid && (urlStatus === "pending" || urlStatus === "success")) {
        try {
          const res = await getPaymentStatus(pid);
          const real = res.data.status;
          if (real === "approved") {
            setResolvedPaymentStatus("success");
            // Re-fetch profiles to show updated subscription
            try {
              const profRes = await getProfiles();
              setProfiles(profRes.data.profiles);
            } catch {}
          } else if (real === "rejected" || real === "failed" || real === "cancelled") {
            setResolvedPaymentStatus("failure");
          } else {
            setResolvedPaymentStatus(urlStatus);
          }
        } catch {
          setResolvedPaymentStatus(urlStatus);
        }
      } else {
        setResolvedPaymentStatus(urlStatus);
      }

      setLoading(false);
    };

    loadData();
  }, []); // eslint-disable-line

  const toggleHistory = (id) =>
    setExpandedHistory((prev) => ({ ...prev, [id]: !prev[id] }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  const plusProfiles = profiles.filter((p) => p.tier !== "free");
  const freeProfiles = profiles.filter((p) => p.tier === "free");
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : "—";

  const isExpired = (endDate) => {
    if (!endDate) return false;
    return new Date(endDate) < new Date();
  };

  const daysLeft = (endDate) => {
    if (!endDate) return 0;
    const diff = new Date(endDate) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const ProfileCard = ({ profile, variant }) => {
    const isPremium = variant === "premium";
    const history = profile.subscription_history || [];
    const showHistory = expandedHistory[profile.id];
    const days = isPremium && profile.subscription ? daysLeft(profile.subscription.end_date) : 0;

    return (
      <div className={`bg-white rounded-xl border overflow-hidden ${
        isPremium ? "border-amber-200 shadow-sm shadow-amber-100" : "border-gray-200"
      }`}>
        {/* Top accent bar */}
        {isPremium && (
          <div className="h-1.5 bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500" />
        )}

        <div className="p-5">
          {/* Profile header */}
          <div className="flex items-center gap-3 mb-4">
            {profile.profile_image ? (
              <img
                src={profile.profile_image}
                alt={profile.profile_name}
                className={`h-11 w-11 rounded-full object-cover ring-2 ${isPremium ? "ring-amber-200" : "ring-gray-100"}`}
              />
            ) : (
              <div className={`flex h-11 w-11 items-center justify-center rounded-full text-white text-base font-bold ring-2 ${
                isPremium
                  ? "bg-gradient-to-br from-amber-400 to-orange-500 ring-amber-200"
                  : "bg-gradient-to-br from-gray-300 to-gray-400 ring-gray-100"
              }`}>
                {profile.profile_name?.charAt(0)?.toUpperCase() || "?"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 truncate">
                {profile.profile_name}
              </h3>
              {isPremium ? (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600">
                  <Crown className="h-3 w-3" />
                  {profile.tier.charAt(0).toUpperCase() + profile.tier.slice(1)}
                </span>
              ) : (
                <span className="text-xs text-gray-400">Free</span>
              )}
            </div>
          </div>

          {/* Subscription info for premium */}
          {isPremium && profile.subscription && (
            <div className="bg-amber-50/60 rounded-lg p-3 mb-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Calendar className="h-3.5 w-3.5" />
                  {fmtDate(profile.subscription.start_date)} — {fmtDate(profile.subscription.end_date)}
                </div>
                <div className="flex items-center gap-1.5">
                  <Shield className="h-3.5 w-3.5 text-green-500" />
                  <span className="text-xs font-semibold text-green-600">{t("subs.activeLabel")}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-amber-200/50 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="h-full bg-amber-500 rounded-full transition-all"
                    style={{ width: `${Math.max(5, Math.min(100, (days / 30) * 100))}%` }}
                  />
                </div>
                <span className="text-xs font-medium text-amber-700 whitespace-nowrap">
                  {days} {t("subs.daysLeft")}
                </span>
              </div>
            </div>
          )}

          {/* Upgrade CTA */}
          {(() => {
            const maxTierPos = tiers.length > 0 ? Math.max(...tiers.map((t) => t.position)) : 0;
            const profileTier = tiers.find((t) => t.name === profile.tier);
            const profilePos = profileTier ? profileTier.position : 0;
            const canUpgrade = profilePos < maxTierPos;
            if (!paymentsEnabled) {
              return !isPremium ? <p className="text-xs text-gray-400 text-center">{t("subs.upgradeHint")}</p> : null;
            }
            if (!canUpgrade) return null;
            return (
              <button
                onClick={() => navigate(`/app/upgrade?profile=${profile.slug}`)}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-sm font-semibold transition shadow-sm"
              >
                <ArrowUpCircle className="h-4 w-4" />
                {t("subs.upgrade")}
              </button>
            );
          })()}

          {/* History toggle */}
          {history.length > 0 && (
            <button
              onClick={() => toggleHistory(profile.id)}
              className="w-full flex items-center justify-center gap-1.5 mt-3 pt-3 border-t border-gray-100 text-xs font-medium text-gray-400 hover:text-gray-600 transition"
            >
              <History className="h-3.5 w-3.5" />
              {t("subs.history")} ({history.length})
              {showHistory ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
          )}
        </div>

        {/* Subscription history */}
        {showHistory && history.length > 0 && (
          <div className="border-t border-gray-100 bg-gray-50 px-5 py-3">
            <div className="space-y-1.5">
              {history.map((h, i) => {
                const expired = isExpired(h.end_date);
                return (
                  <div
                    key={i}
                    className="flex items-center justify-between text-xs py-1.5 px-3 bg-white rounded-lg border border-gray-100"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">{fmtDate(h.start_date)}</span>
                      <span className="text-gray-300">→</span>
                      <span className="text-gray-500">{fmtDate(h.end_date)}</span>
                    </div>
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                      expired
                        ? "bg-gray-100 text-gray-400"
                        : "bg-green-100 text-green-700"
                    }`}>
                      {expired ? t("subs.expired") : t("subs.activeLabel")}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-5xl">
      {/* Payment status banner */}
      {resolvedPaymentStatus === "success" && (
        <div className="mb-4 flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
          <CheckCircle className="h-5 w-5 flex-shrink-0" />
          {t("subs.paymentSuccess")}
        </div>
      )}
      {resolvedPaymentStatus === "failure" && (
        <div className="mb-4 flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <XCircle className="h-5 w-5 flex-shrink-0" />
          {t("subs.paymentFailure")}
        </div>
      )}
      {resolvedPaymentStatus === "pending" && (
        <div className="mb-4 flex items-center gap-2 p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-sm text-yellow-700">
          <Clock className="h-5 w-5 flex-shrink-0" />
          {t("subs.paymentPending")}
        </div>
      )}

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t("subs.title")}</h1>
        <p className="text-sm text-gray-500 mt-1">{t("subs.subtitle")}</p>
      </div>

      {/* Active subscriptions */}
      {plusProfiles.length > 0 && (
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
            {t("subs.active")}
          </h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {plusProfiles.map((profile) => (
              <ProfileCard key={profile.id} profile={profile} variant="premium" />
            ))}
          </div>
        </div>
      )}

      {/* Free profiles */}
      {freeProfiles.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
            {t("subs.freeTier")}
          </h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {freeProfiles.map((profile) => (
              <ProfileCard key={profile.id} profile={profile} variant="free" />
            ))}
          </div>
        </div>
      )}

      {plusProfiles.length === 0 && freeProfiles.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Crown className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">{t("subs.allPremium")}</p>
        </div>
      )}
    </div>
  );
}
